import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, Button, Card, Container, Grid, IconButton, Stack, SwipeableDrawer, TextField, Tooltip, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../../../redux/store';
import useSettings from '../../../../../hooks/useSettings';
import Page from '../../../../../components/Page';
import Iconify from '../../../../../components/Iconify';
import HeaderBreadcrumbs from '../../../../../components/HeaderBreadcrumbs';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as Yup from 'yup';
import DataGridTable from '../../../../../components/table/DataGridTable';
import { FormProvider, RHFUploadAvatar } from '../../../../../components/hook-form';
import { LoadingButton } from '@mui/lab';
import { fData } from '../../../../../utils/formatNumber';
import { imageUpload, fileUpload } from '../../../../../inteceptor';
import { toast } from 'react-toastify';
import { getSpecificUserData } from '../../../../../redux/slices/hrRedux/UserManagementRedux/userManagementRedux';
import { addDocument, updateDocument } from '../../../../../redux/slices/hrRedux/UserManagementRedux/userDocumentUploadRedux';
import moment from 'moment';
import Label from '../../../../../components/Label';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { accessVerify, capitalize } from '../../../../../utils/common';

const DOCUMENT_FILE_TYPES = {
  AUDIO: 'audio',
  PDF: 'pdf',
  IMAGE: 'image',
};

const normalizeDocumentFileType = (type) => {
  return Object.values(DOCUMENT_FILE_TYPES).includes(type) ? type : DOCUMENT_FILE_TYPES.IMAGE;
};

const getDocumentTypeUploadType = (documentType) => normalizeDocumentFileType(documentType?.type);

const getLegacyDocumentFileType = (document, documentType) => {
  if (document?.fileType) return normalizeDocumentFileType(document.fileType);
  if (documentType?.type === DOCUMENT_FILE_TYPES.AUDIO) return DOCUMENT_FILE_TYPES.AUDIO;
  if (document?.url?.split('?')?.[0]?.toLowerCase()?.endsWith('.pdf')) return DOCUMENT_FILE_TYPES.PDF;
  return DOCUMENT_FILE_TYPES.IMAGE;
};

export default function UserUploadedFiles({ userId }) {
  const dataModel = {
    url: null,
    documentTypeId: null,
    fileType: DOCUMENT_FILE_TYPES.IMAGE,
  };
  const { themeStretch } = useSettings();
  const [selectedObject, setSelectedObject] = useState(dataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [documentsList, setDocumentsList] = useState([]);

  const { selectedUser, documentTypes, documents } = useSelector((state) => state.user);
  const { addData } = useSelector((state) => state.document);

  useEffect(() => {
    if (userId) dispatch(getSpecificUserData(userId));
  }, [userId]);

  useEffect(() => {
    setDocumentsList(
      documents?.map((value, index) => {
        const resolvedType = documentTypes?.filter((documentType) => documentType?.id === value?.documentTypeId)?.[0];
        return {
          Id: value?.id,
          dateTime: moment(value?.dateTime).format('Do MMMM YYYY, h:mm:ss a'),
          documentType: resolvedType?.name,
          documentFileType: getLegacyDocumentFileType(value, resolvedType),
          url: value?.url,
          status: value?.status,
        };
      })
    );
    setIsModelOpen(false);
  }, [documents]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedObject(dataModel);
      } else {
        setSelectedObject(modelData);
      }
    }
  };

  const NewProductSchema = Yup.object().shape({
    url: Yup.string().required('Please upload document and try again'),
    documentTypeId: Yup.mixed().required('Please select a document type'),
  });

  const defaultValues = useMemo(
    () => ({
      url: selectedObject?.url || '',
      documentTypeId: selectedObject?.documentTypeId || '',
      fileType: selectedObject?.fileType || DOCUMENT_FILE_TYPES.IMAGE,
    }),
    [selectedObject]
  );

  const methods = useForm({
    resolver: yupResolver(NewProductSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const watchedDocTypeId = watch('documentTypeId');
  const fileTypeCategory = useMemo(() => {
    const docType = documentTypes?.find((dt) => dt?.id === watchedDocTypeId?.id || dt?.id === watchedDocTypeId);
    return getDocumentTypeUploadType(docType);
  }, [watchedDocTypeId, documentTypes]);

  useEffect(() => {
    if (isAdd && selectedObject) {
      reset(defaultValues);
    }
    if (!isAdd) {
      reset(defaultValues);
    }
  }, [isAdd, selectedObject]);

  const onSubmit = async (value) => {
    try {
      const tempDataObj = {
        url: value?.url || '',
        documentTypeId: value?.documentTypeId?.id || '',
        userId: selectedUser?.[0]?.id || '',
        fileType: fileTypeCategory,
      };
      if (isAdd) {
        dispatch(addDocument(tempDataObj));
      } else {
        dispatch(updateDocument({ ...tempDataObj, id: selectedObject?.id }));
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getSpecificUserData(userId));
    }
  }, [addData]);

  const HIDDEN_COLUMNS = ['documentFileType'];

  const columns = !documentsList?.[0]
    ? []
    : Object.keys(documentsList?.[0])
        .filter((key) => !HIDDEN_COLUMNS.includes(key))
        .map((value) => {
          return {
            accessorKey: value,
            header: capitalize(value),
          };
        });

  const [rowSelection, setRowSelection] = useState(false);

  const handleDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      imageUpload(
        file,
        'UserPersonalDocuments',
        (data) => {
          if (data?.status) {
            setValue('url', data?.url);
            setValue('fileType', DOCUMENT_FILE_TYPES.IMAGE);
          }
        },
        (errorMessage) => {
          console.error('Upload failed:', errorMessage);
        }
      );
      if (file) {
        setValue('photoURL', Object.assign(file, { preview: URL.createObjectURL(file) }));
      }
    },
    [setValue]
  );

  const handleAudioChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      fileUpload(
        file,
        'UserPersonalDocuments',
        (data) => {
          if (data?.status) {
            setValue('url', data?.url);
            setValue('fileType', DOCUMENT_FILE_TYPES.AUDIO);
          }
        },
        (errorMessage) => {
          console.error('Audio upload failed:', errorMessage);
        }
      );
    },
    [setValue]
  );

  const handlePdfChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      fileUpload(
        file,
        'UserPersonalDocuments',
        (data) => {
          if (data?.status) {
            setValue('url', data?.url);
            setValue('fileType', DOCUMENT_FILE_TYPES.PDF);
          }
        },
        (errorMessage) => {
          console.error('PDF upload failed:', errorMessage);
        }
      );
    },
    [setValue]
  );

  return (
    <Page title='Location List'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading=''
          links={[]}
          action={
            <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(dataModel, 'add')}>
              Add Document
            </Button>
          }
        />

        <DataGridTable
          name={'User uploaded Files'}
          data={documentsList}
          column={columns.map((value) => {
            if (value?.accessorKey === 'url') {
              return {
                ...value,
                Cell: ({ cell, row }) => {
                  const isAudio = row?.original?.documentFileType === 'audio';
                  const isPdf = row?.original?.documentFileType === 'pdf';
                  return isAudio ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <audio controls src={cell.getValue()} style={{ height: 32, maxWidth: 220 }} />
                      <Tooltip title='Download'>
                        <IconButton size='small' component='a' href={cell.getValue()} target='_blank' rel='noreferrer' download>
                          <Iconify icon='eva:download-outline' sx={{ width: 16, height: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : isPdf ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button size='small' variant='outlined' component='a' href={cell.getValue()} target='_blank' rel='noreferrer' startIcon={<Iconify icon='eva:file-text-outline' />}>
                        Open PDF
                      </Button>
                      <Tooltip title='Download'>
                        <IconButton size='small' component='a' href={cell.getValue()} target='_blank' rel='noreferrer' download>
                          <Iconify icon='eva:download-outline' sx={{ width: 16, height: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <a href={cell.getValue()} target='_blank' rel='noreferrer'>
                      <img src={cell.getValue()} alt={cell.getValue()} />
                    </a>
                  );
                },
              };
            } else if (value?.accessorKey === 'status') {
              return {
                ...value,
                Cell: ({ cell }) => (
                  <Label color={cell.getValue() === 'approved' ? 'success' : cell.getValue() === 'declined' ? 'error' : 'warning'} sx={{ textTransform: 'capitalize' }}>
                    {cell.getValue()}
                  </Label>
                ),
              };
            } else {
              return value;
            }
          })}
          isLoading={false}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          enableRowActions={accessVerify('USER_DOCUMENTS_STATUS_UPDATE')}
          renderRowActionItems={(value, closeMenu) => {
            return (
              accessVerify('USER_DOCUMENTS_STATUS_UPDATE') && (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                  {['approved', 'declined'].includes(value?.original?.status) && (
                    <Tooltip title={'Set to Pending'}>
                      <IconButton
                        color='warning'
                        onClick={() => {
                          dispatch(
                            updateDocument({
                              ...value?.original,
                              status: 'pending',
                              documentTypeId: value?.original?.documentTypeId?.id,
                            })
                          );
                        }}
                      >
                        <PendingIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {['pending', 'declined'].includes(value?.original?.status) && (
                    <Tooltip title={'Set to Approved'}>
                      <IconButton
                        color='success'
                        onClick={() => {
                          dispatch(
                            updateDocument({
                              ...value?.original,
                              status: 'approved',
                              documentTypeId: value?.original?.documentTypeId?.id,
                            })
                          );
                        }}
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {['pending', 'approved'].includes(value?.original?.status) && (
                    <Tooltip title={'Set to Declined'}>
                      <IconButton
                        color='error'
                        onClick={() => {
                          dispatch(
                            updateDocument({
                              ...value?.original,
                              status: 'declined',
                              documentTypeId: value?.original?.documentTypeId?.id,
                            })
                          );
                        }}
                      >
                        <ThumbDownAltIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )
            );
          }}
        />

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Document</h1>
          </Box>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={8}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <Card sx={{ p: 3 }}>
                    {fileTypeCategory === DOCUMENT_FILE_TYPES.AUDIO ? (
                      <Stack spacing={2} alignItems='center' sx={{ py: 2 }}>
                        <Iconify icon='eva:mic-outline' sx={{ width: 48, height: 48, color: 'text.secondary' }} />
                        <Typography variant='body2' color='text.secondary' textAlign='center'>
                          Allowed *.mp3, *.wav, *.m4a, *.ogg
                          <br /> max size of 100 MB
                        </Typography>
                        <Button variant='outlined' component='label' startIcon={<Iconify icon='eva:upload-outline' />}>
                          Select Recording
                          <input hidden type='file' accept='audio/mpeg,audio/wav,audio/mp4,audio/ogg,audio/*' onChange={handleAudioChange} />
                        </Button>
                        <Controller
                          name='url'
                          control={control}
                          render={({ field }) =>
                            field.value ? (
                              <Box sx={{ width: '100%', pt: 1 }}>
                                <audio controls src={field.value} style={{ width: '100%' }} />
                              </Box>
                            ) : null
                          }
                        />
                      </Stack>
                    ) : fileTypeCategory === DOCUMENT_FILE_TYPES.PDF ? (
                      <Stack spacing={2} alignItems='center' sx={{ py: 2 }}>
                        <Iconify icon='eva:file-text-outline' sx={{ width: 48, height: 48, color: 'text.secondary' }} />
                        <Typography variant='body2' color='text.secondary' textAlign='center'>
                          Allowed *.pdf
                          <br /> max size of 100 MB
                        </Typography>
                        <Button variant='outlined' component='label' startIcon={<Iconify icon='eva:upload-outline' />}>
                          Select PDF
                          <input hidden type='file' accept='application/pdf,.pdf' onChange={handlePdfChange} />
                        </Button>
                        <Controller
                          name='url'
                          control={control}
                          render={({ field }) =>
                            field.value ? (
                              <Button size='small' variant='outlined' component='a' href={field.value} target='_blank' rel='noreferrer' startIcon={<Iconify icon='eva:file-text-outline' />}>
                                Open PDF
                              </Button>
                            ) : null
                          }
                        />
                      </Stack>
                    ) : (
                      <RHFUploadAvatar
                        name='url'
                        accept='image/*'
                        maxSize={5045728}
                        onDrop={handleDrop}
                        helperText={
                          <Typography
                            variant='caption'
                            sx={{
                              mt: 2,
                              mx: 'auto',
                              display: 'block',
                              textAlign: 'center',
                              color: 'text.secondary',
                            }}
                          >
                            Allowed *.jpeg, *.jpg, *.png, *.gif
                            <br /> max size of {fData(1048576)}
                          </Typography>
                        }
                      />
                    )}
                  </Card>
                  <Card sx={{ p: 3 }}>
                    <Stack spacing={3}>
                      <Controller
                        name='documentTypeId'
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            onChange={(event, newValue) => {
                              const previousFileType = getDocumentTypeUploadType(field.value);
                              const nextFileType = getDocumentTypeUploadType(newValue);
                              if (previousFileType !== nextFileType) {
                                setValue('url', '');
                              }
                              setValue('fileType', nextFileType);
                              field.onChange(newValue);
                            }}
                            options={documentTypes?.map((value) => {
                              return {
                                ...value,
                                label: value?.name,
                                value: value?.id,
                              };
                            })}
                            renderInput={(params) => <TextField label='Document Type' {...params} />}
                          />
                        )}
                      />
                    </Stack>
                  </Card>
                </Stack>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ p: 3 }}>
                  <Stack spacing={3} sx={{ p: 3 }}>
                    <LoadingButton type='submit' variant='contained' size='large' loading={isSubmitting} style={{ width: '100%' }}>
                      {isAdd ? 'Add Document' : 'Save Changes'}
                    </LoadingButton>
                    <Button color='info' variant='outlined' size='large' loading={isSubmitting} style={{ width: '100%' }} onClick={() => manageModel(dataModel, 'add')}>
                      Close
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
          </FormProvider>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
